const CONNECT_JS_URL = 'https://connect-js.stripe.com/v1.0/connect.js';

let connectJsPromise;

const stripeConnectReady = () =>
    window.StripeConnect && typeof window.StripeConnect.init === 'function';

export const loadStripeConnect = () => {
    if (stripeConnectReady()) {
        return Promise.resolve(window.StripeConnect);
    }

    if (connectJsPromise) {
        return connectJsPromise;
    }

    connectJsPromise = new Promise((resolve, reject) => {
        let script = document.querySelector(`script[src="${CONNECT_JS_URL}"]`);

        const handleLoad = () => {
            if (stripeConnectReady()) {
                resolve(window.StripeConnect);
                return;
            }

            reject(new Error('Stripe Connect loaded without exposing its initializer.'));
        };

        const handleError = () => {
            reject(new Error('Failed to load Stripe Connect.'));
        };

        if (!script) {
            script = document.createElement('script');
            script.src = CONNECT_JS_URL;
            script.async = true;
            document.head.appendChild(script);
        }

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });
    }).catch(error => {
        // A failed script must not leave every in-page retry using a rejected promise.
        connectJsPromise = undefined;
        document.querySelector(`script[src="${CONNECT_JS_URL}"]`)?.remove();
        throw error;
    });

    return connectJsPromise;
};

export const initializeStripeConnect = async ({
    publishableKey,
    fetchClientSecret,
    appearance,
}) => {
    const StripeConnect = await loadStripeConnect();

    return StripeConnect.init({
        publishableKey,
        fetchClientSecret,
        appearance,
    });
};

export const createStripeConnectComponent = async (connectInstance, componentName) => {
    const tagName = `stripe-connect-${componentName}`;
    const element = document.createElement(tagName);

    await window.customElements.whenDefined(tagName);

    if (typeof element.setConnector !== 'function') {
        throw new Error(`Stripe Connect component "${componentName}" is unavailable.`);
    }

    element.setConnector(connectInstance.connect);
    return element;
};
